import { useMemo, useRef, useState } from "react";
import { MDBIcon } from "mdbreact";
import { brandsOf } from "../../brandedProducts/select";

/**
 * Type-ahead for the generic supplies of the phase.
 * A generic may be tagged before its brand exists; it just cannot be
 * auto-consumed until a brand is added in Branded Products.
 */
export default function SupplyPicker({ options, products, onAdd, disabled }) {
  const [search, setSearch] = useState(""),
    [isOpen, setIsOpen] = useState(false),
    inputRef = useRef(null);

  const results = useMemo(() => {
    const key = search.trim().toLowerCase();
    return (
      options
        .filter(
          ({ name = "", abbreviation = "", subname = "", section = "" }) =>
            !key ||
            [name, abbreviation, subname, section].some((value) =>
              value.toLowerCase().includes(key),
            ),
        )
        .map((generic) => ({
          generic,
          brands: brandsOf(products, generic.id).length,
        }))
        // available first, then by name
        .sort(
          (a, b) =>
            (b.brands > 0) - (a.brands > 0) ||
            a.generic.name.localeCompare(b.generic.name),
        )
        .slice(0, 12)
    );
  }, [options, products, search]);

  const pick = ({ generic, brands }) => {
    onAdd(generic);
    setSearch("");
    inputRef.current?.focus();
  };

  return (
    <div className="position-relative">
      <div className="position-relative">
        <MDBIcon
          icon="plus"
          className="position-absolute text-primary"
          style={{ top: "50%", left: 12, transform: "translateY(-50%)" }}
        />
        <input
          ref={inputRef}
          className="form-control"
          style={{ paddingLeft: 34 }}
          placeholder="Add supply: type a name or abbreviation (e.g. SYR, EDTA)"
          value={search}
          disabled={disabled}
          onChange={({ target }) => {
            setSearch(target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          // delay so a click on a result still registers
          onBlur={() => setTimeout(() => setIsOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setIsOpen(false);
            if (e.key === "Enter") {
              e.preventDefault();
              const first = results[0];
              if (first) pick(first);
            }
          }}
        />
      </div>

      {isOpen && (
        <div
          className="position-absolute bg-white border rounded z-depth-1 w-100"
          style={{ zIndex: 10, maxHeight: 320, overflowY: "auto", top: "100%" }}
        >
          {!results.length && (
            <div className="p-2 small text-muted">
              No supply found for this phase. It may be reusable (monthly
              dispense) or already tagged.
            </div>
          )}
          {results.map((result) => {
            const { generic, brands } = result;
            return (
              <div
                key={generic.id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(result)}
                className={`px-2 py-1 border-bottom d-flex justify-content-between align-items-center ${
                  brands ? "" : "text-muted"
                }`}
                style={{ cursor: "pointer" }}
                title={
                  brands
                    ? "Add"
                    : "Add (no brand yet: add one in Branded Products to auto-consume)"
                }
              >
                <div className="text-truncate mr-2">
                  <code className="mr-1">{generic.abbreviation}</code>
                  {generic.name}
                  <small className="text-muted"> · {generic.section}</small>
                </div>
                {brands ? (
                  <span className="badge badge-success">
                    {brands} brand{brands > 1 ? "s" : ""}
                  </span>
                ) : (
                  <span className="badge badge-light text-danger">
                    no brand
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
